<template>
	<div class="subscription-root">
		<div class="switch-container">
			<div class="billing-toggle-container">
				<v-btn-toggle
					v-model="monthlySubscription"
					mandatory
					density="compact"
				>
					<v-btn
						:value="true"
						size="small"
						>Monthly</v-btn
					>
					<v-btn
						:value="false"
						size="small"
						>Yearly</v-btn
					>
				</v-btn-toggle>
			</div>
		</div>

		<div class="sgnl-flex sgnl-justify-center sgnl-flex-column sgnl-gap-16">
			<TierComparisonTable
				:monthlySubscription="monthlySubscription"
				:userSubscription="userStore.currentUserSubscriptions"
				@subscribe-clicked="subscribeButtonClick"
				@free-trial-clicked="freeTrialStart"
				@customer-portal="$emit('customer-portal')"
			/>

			<div
				v-if="!userStore.tierCatalogLoadFailed"
				class="students-card sgnl-p-16 sgnl-mx-auto sgnl-flex sgnl-flex-column"
			>
				<div class="students-card-text">
					<div class="sgnl-subtitle-medium sgnl-text-color-default">Students and Academics</div>
					<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
						If you are a student or an academic, you are eligible for free access to the Developer Tier.
						Please sign-up with your institutional email and use the contact form to request access.
					</div>
				</div>
				<v-btn
					variant="flat"
					color="primary"
					class="sgnl-align-self-start"
					@click="subscribeButtonClick('academic-research-tier')"
					>Contact Us</v-btn
				>
			</div>
		</div>

		<!-- RESULT DIALOG -->

		<v-dialog
			v-model="resultDialog"
			width="400"
		>
			<v-card class="result-dialog-card sgnl-p-16">
				<div class="sgnl-flex sgnl-flex-column sgnl-gap-16">
					<div class="sgnl-flex sgnl-flex-column sgnl-gap-8">
						<v-card-title
							v-if="result === 'success' || result === 'free-trial'"
							class="sgnl-h6-medium sgnl-text-color-default sgnl-flex sgnl-align-center sgnl-gap-8"
						>
							<CheckmarkFilled24 class="sgnl-icon-color-success" />{{ successTitle }}</v-card-title
						>
						<v-card-title
							v-else-if="result === 'info'"
							class="sgnl-h6-medium sgnl-text-color-default sgnl-flex sgnl-align-center sgnl-gap-8"
						>
							<Information16 class="sgnl-icon-color-secondary" />Welcome back
						</v-card-title>
						<v-card-title
							v-else
							class="sgnl-h6-medium sgnl-text-color-default sgnl-flex sgnl-align-center sgnl-gap-8"
						>
							<CloseOutline16 class="sgnl-icon-color-error" />Checkout Failed
						</v-card-title>

						<v-card-text
							v-if="result === 'success' || result === 'info' || result === 'free-trial'"
							class="sgnl-text-left"
						>
							{{ successMessage }}
						</v-card-text>
						<v-card-text
							v-else
							class="sgnl-text-left sgnl-text-color-secondary"
						>
							The checkout process did not complete. If this persists, contact support at
							<a
								href="mailto:developer-support@signaloid.com"
								class="support-email-link sgnl-text-color-brand"
								>developer-support@signaloid.com</a
							>
						</v-card-text>
					</div>
					<v-card-actions class="sgnl-justify-start">
						<v-btn
							variant="flat"
							color="primary"
							@click="dialogOKButtonHandler"
						>
							OK
						</v-btn>
					</v-card-actions>
				</div>
			</v-card>
		</v-dialog>

		<v-dialog
			v-model="interestFormDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			@click:outside="interestFormDialog = false"
			@keydown.esc="interestFormDialog = false"
		>
			<InterestFrom
				:passedPlan="interestFormProps.plan"
				:passedEmail="interestFormProps.email"
				:passedFullname="interestFormProps.fullname"
				@form-submitted="handleInterestFormSubmitted"
				@form-submission-failed="handleInterestFormSubmissionFailed"
				@close-dialog="interestFormDialog = false"
			/>
		</v-dialog>

		<v-dialog
			v-model="contactSalesFormDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			@click:outside="contactSalesFormDialog = false"
			@keydown.esc="contactSalesFormDialog = false"
		>
			<ContactSalesForm
				:passedPlan="contactSalesFormProps.plan"
				:passedEmail="contactSalesFormProps.email"
				:passedFullname="contactSalesFormProps.fullname"
				@form-submitted="handleContactSalesFormSubmitted"
				@form-submission-failed="handleContactSalesFormSubmissionFailed"
				@close-dialog="contactSalesFormDialog = false"
			/>
		</v-dialog>

		<v-dialog
			v-model="manageSubscriptionFormDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			@click:outside="manageSubscriptionFormDialog = false"
			@keydown.esc="manageSubscriptionFormDialog = false"
		>
			<ManageSubscriptionForm
				:passedCurrentTier="manageSubscriptionFormProps.currentTier"
				:passedEmail="manageSubscriptionFormProps.email"
				:passedFullname="manageSubscriptionFormProps.fullname"
				@form-submitted="handleManageSubscriptionFormSubmitted"
				@form-submission-failed="handleManageSubscriptionFormSubmissionFailed"
				@close-dialog="manageSubscriptionFormDialog = false"
			/>
		</v-dialog>

		<v-dialog
			v-model="checkoutRedirectDialog"
			:width="$vuetify.display.mobile ? '95%' : '500px'"
			persistent
		>
			<v-card class="portal-redirect-card sgnl-p-16">
				<v-card-text class="sgnl-flex sgnl-align-center sgnl-gap-12">
					<v-progress-circular />
					<span>Redirecting you to Subscription portal...</span>
				</v-card-text>
			</v-card>
		</v-dialog>

		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>

		<v-snackbar
			v-model="errorSnackbar"
			:timeout="6000"
			class="snackbar-error"
		>
			<div class="snackbar-icon-container snackbar-error">
				<ErrorFilled16 />
			</div>
			{{ errorMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="errorSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent } from "vue";

// Carbon Icons
import Information16 from "@carbon/icons-vue/es/information/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import CheckmarkFilled24 from "@carbon/icons-vue/es/checkmark--filled/24";
import Close16 from "@carbon/icons-vue/es/close/16";
import CloseOutline16 from "@carbon/icons-vue/es/close--outline/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";
import { fetchAuthSession } from "aws-amplify/auth";
import * as Sentry from "@sentry/vue";
import { groupCode, tierFromCode, AccountTierDetail, AccountGroupCode } from "@/js/tierconfig";
import { postCheckoutSession, getStripeCheckoutPublishableKey } from "@/js/signaloidClient";
import { sdk } from "@/js/sdk";
import { useUserStore } from "@/stores/user";

import moment from "moment";

import InterestFrom from "@/components/InterestForm.vue";
import ContactSalesForm from "@/components/ContactSalesForm.vue";
import ManageSubscriptionForm from "@/components/ManageSubscriptionForm.vue";
import TierComparisonTable from "@/components/TierComparisonTable.vue";

type ComponentData = {
	sessionId: string;
	message: string;
	result: string;
	resultDialog: boolean;
	successTitle: string;
	stripePublishableKey: string;
	userHadTierBeforeMount: boolean;
	userHasTier: boolean;
	userCognitoGroups: string[];
	isUnmounting: boolean;
	interestFormDialog: boolean;
	contactSalesFormDialog: boolean;
	manageSubscriptionFormDialog: boolean;
	monthlySubscription: boolean;
	checkoutRedirectDialog: boolean;
	successSnackbar: boolean;
	successMessage: string;
	errorSnackbar: boolean;
	errorMessage: string;
	interestFormProps: {
		plan: string;
		fullname: string;
		email: string;
	};
	contactSalesFormProps: {
		plan: string;
		fullname: string;
		email: string;
	};
	manageSubscriptionFormProps: {
		currentTier: string;
		fullname: string;
		email: string;
	};
};

export default defineComponent({
	name: "Subscription",
	components: {
		InterestFrom,
		ContactSalesForm,
		ManageSubscriptionForm,
		TierComparisonTable,
		Information16,
		CheckmarkFilled16,
		CheckmarkFilled24,
		Close16,
		CloseOutline16,
		ErrorFilled16,
	},
	data: (): ComponentData => ({
		sessionId: "",
		message: "",
		result: "",
		resultDialog: false,
		successTitle: "Subscription Successful",
		stripePublishableKey: "",
		userHadTierBeforeMount: false,
		userHasTier: false,
		userCognitoGroups: [],
		isUnmounting: false,
		interestFormDialog: false,
		contactSalesFormDialog: false,
		manageSubscriptionFormDialog: false,
		monthlySubscription: true,
		checkoutRedirectDialog: false,
		successSnackbar: false,
		successMessage: "",
		errorSnackbar: false,
		errorMessage: "",
		interestFormProps: {
			plan: "",
			fullname: "",
			email: "",
		},
		contactSalesFormProps: {
			plan: "",
			fullname: "",
			email: "",
		},
		manageSubscriptionFormProps: {
			currentTier: "",
			fullname: "",
			email: "",
		},
	}),
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	emits: {
		"customer-portal": () => true,
		"session-refresh": (_sess: any) => true,
	},
	computed: {
		tiers(): AccountTierDetail[] {
			return this.userStore.tierDetails;
		},
		userSubscriptionTierCode: function (): "" | AccountGroupCode {
			return groupCode(this.userStore.currentUserSubscriptions?.Tier ?? "");
		},
		userTierDetails: function (): undefined | AccountTierDetail {
			return this.tiers.find((e) => e.code === this.userSubscriptionTierCode);
		},
		userCanUseCustomerPortal(): boolean {
			return this.userCognitoGroups.includes("CustomerPortalUser");
		},
	},
	watch: {
		interestFormDialog(newFormVisibility, oldFormVisibility) {
			// Guard: Don't trigger analytics during unmount
			if (this.isUnmounting) return;

			if (newFormVisibility !== oldFormVisibility) {
				const eventMessage =
					newFormVisibility === true ? "opened_tier_interest_form" : "closed_tier_interest_form";

				// @ts-ignore
				this.$posthog?.capture(eventMessage, {
					interestedInPlan: this.interestFormProps.plan,
				});
			}
		},
		contactSalesFormDialog(newFormVisibility, oldFormVisibility) {
			// Guard: Don't trigger analytics during unmount
			if (this.isUnmounting) return;

			if (newFormVisibility !== oldFormVisibility) {
				const eventMessage =
					newFormVisibility === true ? "opened_contact_sales_form" : "closed_contact_sales_form";

				// @ts-ignore
				this.$posthog?.capture(eventMessage, {
					interestedInPlan: this.contactSalesFormProps.plan,
				});
			}
		},
		manageSubscriptionFormDialog(newFormVisibility, oldFormVisibility) {
			// Guard: Don't trigger analytics during unmount
			if (this.isUnmounting) return;

			if (newFormVisibility !== oldFormVisibility) {
				const eventMessage =
					newFormVisibility === true ? "opened_manage_subscription_form" : "closed_manage_subscription_form";

				// @ts-ignore
				this.$posthog?.capture(eventMessage, {
					currentTier: this.manageSubscriptionFormProps.currentTier,
				});
			}
		},
		monthlySubscription(newValue) {
			// @ts-ignore
			this.$posthog?.capture("billing_cycle_toggled", { selected_cycle: newValue ? "monthly" : "yearly" });
		},
	},
	methods: {
		async postCheckoutSession(price_id) {
			try {
				const response = await postCheckoutSession(price_id);
				// console.log(response);
				this.sessionId = response.data["SessionID"];
				// @ts-ignore
				this.$posthog?.capture("checkout_session_started", {
					billing_cycle: this.monthlySubscription ? "monthly" : "yearly",
				});
				// console.log(this.sessionId);
			} catch (error) {
				console.log(error);
				Sentry.captureException(error);
				// @ts-ignore
				this.message = error?.message;
			}
		},
		async postUserFreeTier() {
			try {
				await sdk.subscriptions.update({ Tier: "FreeTier" });
				this.result = "success";
				// @ts-ignore
				this.$posthog?.capture("free_trial_started", { tier: "FreeTier" });
			} catch (error) {
				console.log(error);
				Sentry.captureException(error);
				this.result = "canceled";
			}
			await this.forceRefreshUserSession();
			this.resultDialog = true;
		},
		async subscribeButtonClick(code) {
			// @ts-ignore
			this.$posthog?.capture("subscription_button_clicked", {
				currentUserTier: this.userSubscriptionTierCode,
				planClicked: code,
			});

			switch (code) {
				case "free-tier":
					if (this.userCanUseCustomerPortal) {
						this.$emit("customer-portal");
					} else {
						await this.postUserFreeTier();
					}
					break;

				case "academic-research-tier":
					this.initiateContactUs("academic-research-tier");
					break;

				case "developer-tier":
				case "pro-tier":
					if (this.userCanUseCustomerPortal) {
						this.$emit("customer-portal");
					} else {
						this.checkoutRedirectDialog = true;
						// @ts-ignore FIXME:
						const stripePriceId = this.monthlySubscription
							? tierFromCode(code)?.monthlySubscriptionStripePriceId
							: tierFromCode(code)?.yearlySubscriptionStripePriceId;
						await this.postCheckoutSession(stripePriceId);
						this.redirectToCheckout();
					}
					break;

				case "enterprise-tier-s":
					// Check if user is already on this tier (managing) or subscribing
					if (this.userSubscriptionTierCode === "enterprise-tier-s") {
						this.initiateManageSubscription("enterprise-tier-s");
					} else {
						this.initiateContactUs("enterprise-tier-s");
					}
					break;
				case "enterprise-tier-m":
					// Check if user is already on this tier (managing) or subscribing
					if (this.userSubscriptionTierCode === "enterprise-tier-m") {
						this.initiateManageSubscription("enterprise-tier-m");
					} else {
						this.initiateContactUs("enterprise-tier-m");
					}
					break;
				case "enterprise-tier-l":
					// Check if user is already on this tier (managing) or subscribing
					if (this.userSubscriptionTierCode === "enterprise-tier-l") {
						this.initiateManageSubscription("enterprise-tier-l");
					} else {
						this.initiateContactUs("enterprise-tier-l");
					}
					break;

				default:
					break;
			}
		},
		async freeTrialStart(stopSubscribeButtonLoading) {
			try {
				await this.userStore.startFreeTrial();
				stopSubscribeButtonLoading();
				// I know currentUserSubscriptions!.FreeTrial will exists because we just called
				// this.userStore.startFreeTrial(); and it succeeded.
				const friendlyTrialEndDate = moment(
					this.userStore.currentUserSubscriptions!.FreeTrial!.TrialEnd! * 1000,
				).format("MMMM D, YYYY");

				this.result = "free-trial";
				this.successTitle = "Free trial activated";
				this.successMessage =
					"We have activated your two-month free trial for the Developer+API tier. " +
					`The free trial ends on ${friendlyTrialEndDate}. Enjoy!`;
			} catch {
				this.result = "canceled";
			}
			this.resultDialog = true;
		},
		async initiateContactUs(planClicked) {
			try {
				const session = await fetchAuthSession();
				const idToken = session.tokens?.idToken;
				const fullname = (idToken?.payload?.["name"] as string) ?? "";
				const email = (idToken?.payload?.["email"] as string) ?? "";

				// Check if it's an Enterprise tier
				const isEnterpriseTier = ["enterprise-tier-s", "enterprise-tier-m", "enterprise-tier-l"].includes(
					planClicked,
				);

				if (isEnterpriseTier) {
					// Use Contact Sales form for Enterprise tiers
					this.contactSalesFormProps.fullname = fullname;
					this.contactSalesFormProps.email = email;

					switch (planClicked) {
						case "enterprise-tier-s":
							this.contactSalesFormProps.plan = "Enterprise S";
							break;
						case "enterprise-tier-m":
							this.contactSalesFormProps.plan = "Enterprise M";
							break;
						case "enterprise-tier-l":
							this.contactSalesFormProps.plan = "Enterprise L";
							break;
					}

					this.contactSalesFormDialog = true;
				} else {
					// Use Interest form for non-Enterprise tiers
					this.interestFormProps.fullname = fullname;
					this.interestFormProps.email = email;

					switch (planClicked) {
						case "academic-research-tier":
							this.interestFormProps.plan = "Developer";
							break;
						case "pro-tier":
							this.interestFormProps.plan = "Pro";
							break;
						default:
							break;
					}

					this.interestFormDialog = true;
				}
			} catch (error) {
				console.error("Could not get current auth session.");
				Sentry.captureException(error);
				// Even if setting up the props failed, we can still show the appropriate form
				const isEnterpriseTier = ["enterprise-tier-s", "enterprise-tier-m", "enterprise-tier-l"].includes(
					planClicked,
				);
				if (isEnterpriseTier) {
					this.contactSalesFormDialog = true;
				} else {
					this.interestFormDialog = true;
				}
			}
		},
		async initiateManageSubscription(tierCode) {
			try {
				const session = await fetchAuthSession();
				const idToken = session.tokens?.idToken;
				const fullname = (idToken?.payload?.["name"] as string) ?? "";
				const email = (idToken?.payload?.["email"] as string) ?? "";

				this.manageSubscriptionFormProps.fullname = fullname;
				this.manageSubscriptionFormProps.email = email;

				switch (tierCode) {
					case "enterprise-tier-s":
						this.manageSubscriptionFormProps.currentTier = "Enterprise S";
						break;
					case "enterprise-tier-m":
						this.manageSubscriptionFormProps.currentTier = "Enterprise M";
						break;
					case "enterprise-tier-l":
						this.manageSubscriptionFormProps.currentTier = "Enterprise L";
						break;
				}

				this.manageSubscriptionFormDialog = true;
			} catch (error) {
				console.error("Could not get current auth session.");
				Sentry.captureException(error);
				// Even if setting up the props failed, we can still show the form
				this.manageSubscriptionFormDialog = true;
			}
		},
		handleInterestFormSubmitted() {
			this.interestFormDialog = false;
			this.successMessage = "Form submitted successfully! We'll be in touch soon.";
			this.successSnackbar = true;
		},
		handleContactSalesFormSubmitted() {
			this.contactSalesFormDialog = false;
			this.successMessage =
				"Contact request submitted successfully! Our sales team will reach out to you shortly.";
			this.successSnackbar = true;
		},
		handleManageSubscriptionFormSubmitted() {
			this.manageSubscriptionFormDialog = false;
			this.successMessage = "Request submitted successfully! Our team will contact you soon.";
			this.successSnackbar = true;
		},
		handleInterestFormSubmissionFailed() {
			this.errorMessage = "Submission failed. Please check your internet connection and try again shortly.";
			this.errorSnackbar = true;
		},
		handleContactSalesFormSubmissionFailed() {
			this.errorMessage = "Submission failed. Please check your internet connection and try again shortly.";
			this.errorSnackbar = true;
		},
		handleManageSubscriptionFormSubmissionFailed() {
			this.errorMessage = "Submission failed. Please check your internet connection and try again shortly.";
			this.errorSnackbar = true;
		},
		async checkUserGroups() {
			try {
				const session = await fetchAuthSession();
				const accessToken = session.tokens?.accessToken;
				const groups = (accessToken?.payload?.["cognito:groups"] as string[]) ?? [];
				if (groups.length > 0) {
					this.userCognitoGroups = groups;
					if (
						groups.includes("FreeTier") ||
						groups.includes("AcademicTier") ||
						groups.includes("DeveloperTier") ||
						groups.includes("ProTier") ||
						groups.includes("EnterpriseTier") ||
						groups.includes("VirtualServerTier")
					) {
						this.userHasTier = true;
					} else {
						this.userHasTier = false;
					}
				}
			} catch (error) {
				console.log(error);
				Sentry.captureException(error);
			}
		},
		async redirectToCheckout() {
			/*
			 *  Redirect to Stripe's secure checkout page.
			 */
			// Guard: Don't redirect if component is unmounting
			if (this.isUnmounting) {
				return;
			}

			try {
				const { loadStripe } = await import("@stripe/stripe-js");
				const stripe = await loadStripe(this.stripePublishableKey, { locale: "en-GB" });
				if (stripe && this.sessionId) {
					await stripe.redirectToCheckout({ sessionId: this.sessionId });
				} else {
					this.checkoutRedirectDialog = false;
				}
			} catch (error) {
				console.error("Failed to redirect to Stripe checkout:", error);
				this.checkoutRedirectDialog = false;
			}
		},
		// @ts-ignore
		async forceRefreshUserSession() {
			// console.log("Force-refresh session.");
			try {
				const [sess] = await Promise.all([
					this.userStore.refreshCurrentUserSession(),
					this.userStore.fetchTierCatalog(),
				]);
				const accessToken = (sess as any)?.tokens?.accessToken;
				const groups = (accessToken?.payload?.["cognito:groups"] as string[]) ?? [];

				if (groups.length > 0) {
					if (this.userTierDetails?.fullTitle) {
						switch (this.result) {
							case "success":
								this.successMessage = `You have subscribed to the ${this.userTierDetails?.fullTitle}.`;
								break;
							default:
								this.successMessage = `You are subscribed to the ${this.userTierDetails?.fullTitle}.`;
								break;
						}
					}

					// @ts-ignore
					this.$posthog?.capture("subscription_successful", {
						newUserTier: this.userStore.currentUserSubscriptions?.Tier ?? "",
					});

					this.$emit("session-refresh", sess);
				} else {
					console.error("User does not belong to a subscription tier. How did they end up here?");
					return Promise.reject("User does not belong to a subscription tier. How did they end up here?");
				}
			} catch (error) {
				console.log(error);
				// Skip sending the expected error to Sentry
				if (
					error instanceof Error &&
					error.message === "Unable to refresh session - user or session not found"
				) {
					return; // just ignore
				}
				Sentry.captureException(error);
				// TODO: Somehow signal that the user must refresh tokens. Maybe sign them out.
				// await Auth.signOut();
			}
		},
		dialogOKButtonHandler() {
			this.resultDialog = false;

			// Guard: Don't navigate if component is unmounting
			if (this.isUnmounting) {
				return;
			}

			if (this.result === "success" && this.userHadTierBeforeMount === false && this.userHasTier === true) {
				/*
				 *	Redirect newly subscribed users to the Launch page
				 */
				this.$router.push({ name: "Home" });
			}
		},
	},
	created() {
		if (this.$route.query?.session_id) {
			// @ts-ignore FIXME:
			this.sessionId = this.$route.query?.session_id;
		}
	},
	async beforeMount() {
		try {
			await this.checkUserGroups();
			this.userHadTierBeforeMount = this.userHasTier;
		} catch (error) {
			console.log(error);
			Sentry.captureException(error);
		}
	},
	async mounted() {
		// @ts-ignore
		this.$posthog?.capture("subscription_page_viewed", { current_tier: this.userSubscriptionTierCode });
		// console.log(window.location.origin);
		if (this.$route.hash !== "") {
			const hashResult = this.$route.hash.substring(1);
			console.log(hashResult);
			switch (hashResult) {
				case "success":
					/*
					 *  Force-refresh user attributes.
					 */
					this.forceRefreshUserSession();

					this.result = "success";
					this.resultDialog = true;

					break;

				case "canceled":
					this.$router.replace({ path: "/billing" });
					break;

				case "info": {
					const tierBefore = this.userSubscriptionTierCode;
					await this.forceRefreshUserSession();
					if (this.userSubscriptionTierCode !== tierBefore) {
						this.result = "info";
						this.resultDialog = true;
					} else {
						this.$router.replace({ path: "/billing" });
					}
					break;
				}

				default:
					break;
			}
			// TODO: Remove hash after processing it.
		}

		/*
		 *  Get Stripe Publishable Key from backend.
		 */
		try {
			const response = await getStripeCheckoutPublishableKey();

			// Guard: Don't update if component is unmounting
			if (this.isUnmounting) {
				return;
			}

			const stripeKey = response.data["StripePublicKey"];
			if (stripeKey && typeof stripeKey === "string" && stripeKey.length > 0) {
				this.stripePublishableKey = stripeKey;
			} else {
				console.error("Invalid Stripe publishable key received from API:", stripeKey);
			}
		} catch (error) {
			console.error("Failed to fetch Stripe publishable key:", error);
			Sentry.captureException(error);
		}
	},
	beforeUnmount() {
		// Mark component as unmounting to prevent async updates
		// This is the ONLY thing we should do in beforeUnmount
		// Vue will handle all other cleanup automatically
		this.isUnmounting = true;
	},
});
</script>

<style lang="scss" scoped>
.subscription-root {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}

.switch-container {
	display: flex;
	justify-content: center;
}

.billing-toggle-container {
	display: inline-flex;
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
	overflow: hidden;

	:deep(.v-btn-toggle .v-btn) {
		border: none;
		min-width: auto;
		padding: var(--sgnl-space-4) var(--sgnl-space-12);
	}

	:deep(.v-btn-toggle .v-btn .v-btn__overlay),
	:deep(.v-btn-toggle .v-btn .v-btn__underlay) {
		display: none;
	}

	:deep(.v-btn-toggle .v-btn.v-btn--active) {
		background: var(--sgnl-brand-background);
		color: var(--sgnl-default-primary-text-inverse);
		box-shadow: none;
	}

	:deep(.v-btn-toggle .v-btn.v-btn--active > .v-btn__content) {
		color: var(--sgnl-default-primary-text-inverse);
	}

	:deep(.v-btn-toggle .v-btn.v-btn--active:hover) {
		background: var(--sgnl-brand-background-hover);
	}
}

.students-card {
	border-radius: var(--sgnl-border-radius-6);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	background: var(--sgnl-default-primary-background);
	width: 100%;
	text-align: left;
	gap: var(--sgnl-space-12);
}

.students-card-text {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.result-dialog-card {
	background: var(--sgnl-default-primary-background-mono);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);

	:deep(.v-card-title),
	:deep(.v-card-text),
	:deep(.v-card-actions) {
		padding: 0;
	}
}

.portal-redirect-card {
	background: var(--sgnl-default-primary-background-mono);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
}

.support-email-link {
	text-decoration: underline;
	font: inherit;
}
</style>
